import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type SeoKeywordStatus = "predlagano" | "v_delu" | "izvedeno" | "zavrnjeno";

export type SeoDailyKeyword = {
  keyword: string;
  type: "short" | "long";
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  reason: string;
  potential: number;
  action: "enrich" | "new";
  targetUrl: string | null;
  targetTitle: string | null;
  status: SeoKeywordStatus;
};

export type SeoDailyReport = {
  id: string;
  report_date: string;
  site_url: string | null;
  range_from: string | null;
  range_to: string | null;
  summary: string | null;
  keywords: SeoDailyKeyword[];
  done_notes: string | null;
  created_at: string;
  updated_at: string;
};

export const listSeoDailyReports = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SeoDailyReport[]> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("seo_daily_report")
      .select("*")
      .order("report_date", { ascending: false })
      .limit(60);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({ ...r, keywords: (r.keywords ?? []) as SeoDailyKeyword[] })) as SeoDailyReport[];
  });

export const runSeoDailyNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { runSeoDaily } = await import("./seo-daily.server");
    return await runSeoDaily();
  });

export const setSeoKeywordStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { reportId: string; keyword: string; status: SeoKeywordStatus }) => {
    const allowed: SeoKeywordStatus[] = ["predlagano", "v_delu", "izvedeno", "zavrnjeno"];
    if (!data?.reportId || !data?.keyword || !allowed.includes(data.status)) {
      throw new Error("Neveljavni podatki.");
    }
    return data;
  })
  .handler(async ({ context, data }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row, error: readErr } = await supabaseAdmin
      .from("seo_daily_report")
      .select("keywords")
      .eq("id", data.reportId)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!row) throw new Error("Poročilo ne obstaja.");

    const keywords = ((row.keywords ?? []) as SeoDailyKeyword[]).map((k) =>
      k.keyword === data.keyword ? { ...k, status: data.status } : k,
    );

    const { error } = await supabaseAdmin
      .from("seo_daily_report")
      .update({ keywords, updated_at: new Date().toISOString() })
      .eq("id", data.reportId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const saveSeoDailyNotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { reportId: string; notes: string }) => {
    if (!data?.reportId) throw new Error("Manjka poročilo.");
    return { reportId: data.reportId, notes: String(data.notes ?? "").slice(0, 8000) };
  })
  .handler(async ({ context, data }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("seo_daily_report")
      .update({ done_notes: data.notes, updated_at: new Date().toISOString() })
      .eq("id", data.reportId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
